<template>
  <AppDialog
    :is-open="isOpen"
    :size="dialogSize"
    @cancel="onCancel"
    class="smart-import-modal"
  >
    <template #title>
       <span>
        {{ $t(`${TRANSLATION_KEY_PREFIX}.title`) }}
      </span>
    </template>

    <template #body>
      <template v-if="currentStep === 'upload'">
        <p class="text--medium text-grey-900 font-weight-medium mb-2 mt-2">
          {{ $t(`${TRANSLATION_KEY_PREFIX}.subtitle`) }}
        </p>
        <p class="text-grey-700">
          {{ $t(`${TRANSLATION_KEY_PREFIX}.description`) }}
        </p>

        <div class="my-4">
        <AppDroppableArea
          class="smart-import-dropzone text-center d-flex flex-column justify-center align-center"
          :class="{'drag-in-progress': dragInProgress, 'disabled': selectedFile || importing}"
          :disabled="!!selectedFile || importing"
          @dragover="dragInProgress = true"
          @dragenter="dragInProgress = true"
          @dragleave="dragInProgress = false"
          @ondrop="onFileDrop"
        >
          <template v-if="importing">
            <v-progress-circular
              indeterminate
              size="24"
            />
            <p class="mt-4 font-weight-medium text-grey-800">
              {{ $t(`${TRANSLATION_KEY_PREFIX}.importing`) }}
            </p>
          </template>
          <template v-else>
            <div class="d-flex flex-column row-gap-4 justify-center align-center">
              <app-material-icon
                name="arrow-up-from-line"
                size="lg"
                type="fa"
                :color="dragInProgress ? 'darkblue-500' : (selectedFile ? 'grey-300' : 'grey-800')"
              />
              
              <input
                ref="fileInput"
                class="d-none"
                data-locator="word-file-input"
                type="file"
                accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                @change="onFileSelected"
              />
              
              <span :class="selectedFile ? 'text-grey-400' : 'text-grey-800'">
                {{ $t(`${TRANSLATION_KEY_PREFIX}.dragAndDrop`) }}
              </span>
              
              <app-button
                type="outlined"
                :disabled="!!selectedFile"
                @click="onBrowseClick"
                variant="neutral"
              >
                {{ $t(`${TRANSLATION_KEY_PREFIX}.importFromComputer`) }}
              </app-button>
            </div>
          </template>
        </AppDroppableArea>
        
        <div v-if="selectedFile && !importing" class="mt-4 pa-4 d-flex align-center selected-file-card">
          <div>
            <OfficeIcon
            document-type="word"
            icon-type="document"
            class="mr-2"
            :width="32"
            :height="32"
            />
          </div>
          <div class="flex-grow-1">
            <div class="text-body-1 font-weight-regular text-grey-900">{{ selectedFile.name }}</div>
            <app-filesize-display class="text-body-2 text-grey-600" :value="selectedFile.size" />
          </div>
          <app-button
            type="icon"
            size="small"
            class="ml-auto"
            @click="clearFile"
          >
            <app-material-icon name="xmark" size="sm" type="fa" class="text-grey-600" />
          </app-button>
        </div>
      </div>
      </template>

      <SmartImportPreview 
        v-else-if="currentStep === 'preview' && uploadedFileId"
        :file-id="uploadedFileId"
        :file-name="selectedFile?.name || ''"
        :preview="preview"
      />
      <SmartImportOptions
        v-else-if="currentStep === 'options'"
        v-model="importMode"
      />
    </template>

    <template #footer>
      <app-button
        v-if="currentStep === 'options'"
        type="outlined"
        variant="neutral"
        @click="onPrevious"
      >
        <template #left-icon>
          <app-material-icon name="arrow-left" size="xs" type="fa" />
        </template>
        {{ $t('common.previous') }}
      </app-button>
      
      <div class="ml-auto d-flex">
        <app-button
          class="mr-4"
          type="outlined"
          variant="neutral"
          @click="onClose"
        >
          {{ $t('common.cancel') }}
        </app-button>
        
        <app-button
          v-if="currentStep !== 'options'"
          type="primary"
          variant="brand"
          :disabled="!uploadedFileId || importing"
          @click="onNext"
        >
          {{ $t('common.next') }}
          <template #right-icon>
            <app-material-icon name="arrow-right" size="xs" type="fa" />
          </template>
        </app-button>
        
        <app-button
          v-if="currentStep === 'options'"
          type="primary"
          variant="brand"
          @click="onConfirmImport"
        >
          {{ $t('project.checklist.dialogs.SmartImportModal.importChecklist') }}
        </app-button>
      </div>
    </template>
  </AppDialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'

import AppDialog from '@/common/dialogs/AppDialog.vue'
import AppDroppableArea from '@/common/AppDroppableArea.vue'
import { toast } from '@/common/utils/toast'
import OfficeIcon from '@/common/office/OfficeIcon.vue'
import SmartImportPreview from '@/project/checklist/dialogs/SmartImportPreview.vue'
import SmartImportOptions from '@/project/checklist/dialogs/SmartImportOptions.vue'
import checklistService from '@/services/checklist.service'
import { SET_SMART_IMPORT_MODAL_IS_OPEN } from '@/store/modules/checklist/mutation_types'
import { IMPORT_CHECKLIST_FROM_WORD, GET_CHECKLIST } from '@/store/modules/checklist/action_types'
import { UPLOAD_HIDDEN_FILE } from '@/store/modules/documents/action_types'

const TRANSLATION_KEY_PREFIX = 'project.checklist.dialogs.SmartImportModal'

export default defineComponent({
  name: 'SmartImportModal',
  components: { 
    AppDialog,
    AppDroppableArea,
    OfficeIcon,
    SmartImportPreview,
    SmartImportOptions,
  },
  data() {
    return {
      TRANSLATION_KEY_PREFIX,
      currentStep: 'upload' as 'upload' | 'preview' | 'options',
      dragInProgress: false,
      selectedFile: null as File | null,
      importing: false,
      isFileDialogOpen: false,
      uploadedFileId: null as number | null,
      draftId: null as number | null,
      preview: null as any,
      importMode: 'replace' as 'replace' | 'append',
    }
  },
  computed: {
    ...mapState('checklist', {
      isOpen: 'smartImportModalIsOpen',
    }),
    ...mapGetters('room', ['roomMnemo']),
    dialogSize(): string {
      return this.currentStep === 'preview' || this.currentStep === 'options' ? 'xxxl' : 'xl'
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.resetState()
      }
    },
  },
  methods: {
    ...mapMutations('checklist', [SET_SMART_IMPORT_MODAL_IS_OPEN]),
    ...mapActions('checklist', [IMPORT_CHECKLIST_FROM_WORD, GET_CHECKLIST]),
    ...mapActions('documents', [UPLOAD_HIDDEN_FILE]),
    ...mapActions('checklist', [GET_CHECKLIST]),
    resetState() {
      this.currentStep = 'upload'
      this.selectedFile = null
      this.importing = false
      this.dragInProgress = false
      this.isFileDialogOpen = false
      this.uploadedFileId = null
      this.draftId = null
      this.preview = null
    },
    onBrowseClick() {
      this.isFileDialogOpen = true
      
      const fileInput = this.$refs['fileInput'] as HTMLInputElement
      
      const onWindowFocus = () => {
        setTimeout(() => {
          this.isFileDialogOpen = false
        }, 100)
      }
      
      window.addEventListener('focus', onWindowFocus, { once: true })
      
      fileInput.click()
    },
    onFileSelected(event: Event) {
      this.isFileDialogOpen = false
      const target = event.target as HTMLInputElement
      const files = target.files
      if (files && files.length > 0) {
        this.handleFile(files[0])
      }
      target.value = ''
    },
    async onFileDrop(event: DragEvent) {
      this.dragInProgress = false
      const files = event.dataTransfer?.files
      if (files && files.length > 0) {
        this.handleFile(files[0])
      }
    },
    async handleFile(file: File) {
      const validTypes = [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ]
      const isValidExtension = file.name.endsWith('.docx')
      
      if (!validTypes.includes(file.type) && !isValidExtension) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.errorInvalidFile`))
        return
      }
      
      this.selectedFile = file
      this.importing = true
      
      const fileToUpload: any = {
        name: file.name,
        objectToPost: {
          fileRaw: file,
          fileName: file.name,
          title: file.name,
        },
        extension: file.name.split('.').pop(),
        progress: 0,
        status: 'queued',
      }
      
      try {
        await this.UPLOAD_HIDDEN_FILE({
          file: fileToUpload,
          mnemo: this.roomMnemo,
        })
        
        this.uploadedFileId = fileToUpload.id
      } catch (err) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.errorUpload`))
        console.error('Upload error:', err)
        this.selectedFile = null
      } finally {
        this.importing = false
      }
    },
    clearFile() {
      this.selectedFile = null
      this.uploadedFileId = null
    },
    onCancel() {
      if (this.isFileDialogOpen) {
        return
      }
      this.onClose()
    },
    onClose() {
      this.SET_SMART_IMPORT_MODAL_IS_OPEN(false)
    },
    async onNext() {
      if (!this.uploadedFileId) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.errorNoFile`))
        return
      }

      if (this.currentStep === 'upload') {
        this.currentStep = 'preview'
        try {
          const response = await this.IMPORT_CHECKLIST_FROM_WORD({
            mnemo: this.roomMnemo,
            data: { fileId: this.uploadedFileId },
          })

          this.draftId = response.draftId
          this.preview = response.preview
        } catch (error) {
          toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.errorImport`))
          console.error('Import error:', error)
        }
      } else if (this.currentStep === 'preview') {
        this.currentStep = 'options'
      }
    },

    onPrevious() {
      if (this.currentStep === 'options') {
        this.currentStep = 'preview'
      } else if (this.currentStep === 'preview') {
        this.currentStep = 'upload'
      }
    },

    async onConfirmImport() {
      console.log(this.importMode, this.draftId)
      if (!this.draftId) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.errorNoFile`))
        return
      }
      try {
        await checklistService.importChecklistFromWordConfirm(this.roomMnemo, {
          draftId: this.draftId,
          reset: this.importMode === 'replace',
        })

        await this.GET_CHECKLIST(this.roomMnemo)
        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.importSuccess`))
        this.onClose()
      } catch (error) {
        console.error('Import error:', error)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.errorImport`))
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.smart-import-dropzone {
  border: 1px dashed rgb(var(--v-theme-grey-100));
  border-radius: 12px;
  background-color: rgb(var(--v-theme-grey-25));
  transition: all 0.2s ease;
  min-height: 300px;
  cursor: pointer;
  
  &.drag-in-progress {
    background-color: rgb(var(--v-theme-darkblue-50)) !important;
    outline: 1px dashed rgb(var(--v-theme-darkblue-500)) !important;
    border: none !important;
  }
  
  &.disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
}

.selected-file-card {
  border: 1px solid rgb(var(--v-theme-grey-100));
  border-radius: 8px;
  background-color: rgb(var(--v-theme-grey-25));
}

</style>
